import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const FEATURE_KEYS = [
  { key: "ai", label: "Inteligência Artificial" },
  { key: "appcc", label: "APPCC" },
  { key: "audits", label: "Auditorias" },
  { key: "whatsapp", label: "Notificações WhatsApp" },
  { key: "offline", label: "Modo Offline" },
  { key: "multi_unit", label: "Multi-unidade" },
  { key: "stock_control", label: "Controle de Estoque" },
];

// Recurso ativo = override da empresa, senão o que o plano inclui (stock_control ausente = ativo)
export const resolveFeatures = (company, plan) => {
  const flags = company?.feature_flags || {};
  const planF = plan?.features || {};
  const out = {};
  for (const f of FEATURE_KEYS) {
    if (flags[f.key] !== undefined && flags[f.key] !== null) out[f.key] = !!flags[f.key];
    else if (f.key === "stock_control") out[f.key] = planF.stock_control !== false;
    else out[f.key] = !!planF[f.key];
  }
  return out;
};

export const CompanyFeaturesDialog = ({ company, plans, onOpenChange, onSaved }) => {
  const [planId, setPlanId] = useState("");
  const [flags, setFlags] = useState({});
  const [saving, setSaving] = useState(false);
  const plan = plans.find((p) => p.id === planId);

  useEffect(() => {
    if (company) {
      setPlanId(company.plan_id || "");
      setFlags(resolveFeatures(company, plans.find((p) => p.id === company.plan_id)));
    }
  }, [company, plans]);

  const save = async () => {
    setSaving(true);
    try {
      if (planId && planId !== company.plan_id) {
        await api.patch(`/admin/companies/${company.id}`, { plan_id: planId });
      }
      await api.patch(`/admin/companies/${company.id}/feature-flags`, flags);
      toast.success("Recursos atualizados");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!company} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="company-features-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Recursos ativos — {company?.trade_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="font-body">Plano</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger className="mt-1" data-testid="company-plan-select">
                <SelectValue placeholder="Selecione o plano" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            {FEATURE_KEYS.map((f) => {
              const inPlan = f.key === "stock_control" ? plan?.features?.stock_control !== false : !!plan?.features?.[f.key];
              return (
                <div key={f.key} className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-body">{f.label}</span>
                    {plan && !inPlan && !!flags[f.key] && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">extra</Badge>
                    )}
                  </div>
                  <Switch
                    checked={!!flags[f.key]}
                    onCheckedChange={(v) => setFlags((s) => ({ ...s, [f.key]: v }))}
                    data-testid={`company-feature-${f.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="font-body">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900 text-white font-body" data-testid="company-features-save">
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
